// Mirrors the "Investigations" tab of the doctor console's Order Management
// drawer (src/pages/ipd/pages/doctor-console-ipd/OrderManagementDrawer.js) —
// same endpoints, same payload shapes, same client-side batching — so a lab/
// radiology order placed from this OT tile behaves identically to one placed
// from Order Management → New Order. Only the UI differs.
import axios from 'axios';
import { createLabOrderBatchId, rememberLabOrderBatch } from '../lab-technician/labOrdersShared';

const API_BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  'https://manage-dev.hlthtek.com/app/api/';

const apiHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('accessToken') || ''}`,
  'APP-MODE': sessionStorage.getItem('APP_MODE') || '',
  'ORG-CODE': sessionStorage.getItem('ORG_CODE') || '',
  'USER-ACCESS-KEY': sessionStorage.getItem('USER_ACCESS_KEY') || '',
  USERNAME: sessionStorage.getItem('userName') || '',
});

export const PRIORITY_OPTIONS = ['Routine', 'Urgent', 'STAT', 'ASAP'];
export const SERVICE_TYPE_OPTIONS = ['All Service Types', 'Diagnostic', 'Procedure', 'Imaging', 'Therapeutic'];
export const DEPARTMENT_OPTIONS = ['All Departments', 'Radiology', 'Laboratory', 'Microbiology', 'Pathology', 'Cardiology'];
export const INSTRUCTION_OPTIONS = [
  'Before Breakfast', 'After Breakfast', 'With Breakfast',
  'Before Lunch', 'After Lunch', 'With Lunch',
  'Before Dinner', 'After Dinner', 'With Dinner',
  'Before Food', 'After Food', 'With Food',
  'Empty Stomach', 'Fasting', 'Bedtime', 'As Needed',
];

// Care Plan / Order Management's priority options can arrive lowercase; the
// lab/radiology dashboards filter by exact-cased strings, so normalize here
// the same way Order Management does before it ever reaches the backend.
export const toWirePriority = (raw) => {
  const value = String(raw || '').trim();
  if (!value) return 'Routine';
  const lower = value.toLowerCase();
  if (lower === 'stat' || lower === 'asap') return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// care_plan_structures/ → the "Test" activity's test_items row fields
// (only "instruction" options are used downstream).
function extractTestItemSchema(structures) {
  const testDef = (structures || []).find(
    (item) => String(item?.name || '').toLowerCase().trim() === 'test',
  );
  if (!testDef) return null;
  const testItemsField = (testDef.structure || []).find((f) => f.field === 'test_items');
  const columns = testItemsField?.columns || [];
  const instructionCol = columns.find((c) => c.field === 'instruction');
  return {
    instructionOptions: instructionCol?.options || [],
  };
}

// charge-capture/services/ result → investigation search-result card.
export function mapMedicalServiceToInvestigationCard(item) {
  const id = item?.medical_service_id ?? item?.id ?? null;
  if (id == null) return null;
  return {
    id: String(id),
    medical_service_id: String(id),
    name: item?.term || item?.name || 'Unnamed service',
    code: item?.service_code || item?.code || item?.snomed_code || item?.conceptId || item?.concept_id || '',
    category: item?.groupName || item?.billing_category || item?.categoryChip || '',
    serviceType: item?.service_type || item?.categoryChip || '',
    cpt: '',
    tat: '',
    rate: item?.rate ?? item?.fee ?? null,
    chargeModifiable: item?.chargeModifiable === true || item?.charge_modifiable === true,
    quantityModifiable: item?.quantityModifiable !== false && item?.quantity_modifiable !== false,
    interface: item?.interface || item?.interface_type || '',
    instructions: '',
    _matchScore: Number(item?.match_score) || 0,
    _isHighQuality: Boolean(item?.is_high_quality),
    _raw: item,
  };
}

/** GET care_plan_structures/ — same call the Investigations tab makes for its instruction options. */
export async function fetchInvestigationSchema() {
  const response = await axios.get(`${API_BASE}care_plan_structures/`, { headers: apiHeaders() });
  const structures = Array.isArray(response?.data?.data) ? response.data.data : [];
  return extractTestItemSchema(structures);
}

/**
 * POST add_patient_lab_tests/ — one Sign & Send = one lab batch, remembered
 * client-side (rememberLabOrderBatch) so it shows as one row on the lab
 * dashboard, exactly as Order Management's Investigations tab does.
 */
export async function signAndSendInvestigations({ pendingItems, admissionId, careContextId, doctorName }) {
  const labTestBatchId = createLabOrderBatchId();
  const testPayload = pendingItems.map((item) => ({
    name: item.name,
    code: item.code || '',
    medical_service_id: String(item.medical_service_id || item.id || ''),
    priority: toWirePriority(item.priority),
    lab_test_batch_id: labTestBatchId,
  }));
  const res = await axios.post(
    `${API_BASE}add_patient_lab_tests/`,
    {
      care_context_id: careContextId,
      admission_id: String(admissionId),
      patient_type: 'IPD',
      status: 'NEW',
      doctor_name: doctorName,
      lab_test_batch_id: labTestBatchId,
      priority: testPayload[0]?.priority || 'Routine',
      test: testPayload,
    },
    { headers: apiHeaders() },
  );
  if (res.data?.is_valid === false || String(res.data?.status || '').toLowerCase() === 'failed') {
    throw new Error(res.data?.message || 'Lab order was rejected by the server.');
  }
  rememberLabOrderBatch(admissionId, labTestBatchId, testPayload);
  return res.data;
}
